import {classNames} from '@cohort/shared-frontend/utils/classNames';
import PrimaryButton from '@cohort/wallet/components/button/PrimaryButton';
import {DigitalAssetCard} from '@cohort/wallet/components/space/DigitalAssetCard';
import EmptySection from '@cohort/wallet/components/space/EmptySection';
import useScreenSize from '@cohort/wallet/hooks/useScreenSize';
import useSpaceContext from '@cohort/wallet/hooks/useSpaceContext';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import {getImportNftRoute} from '@cohort/wallet/lib/Pages';
import type {OrderWDto} from '@cohort/wallet-schemas/order';
import type {OwnershipWDto} from '@cohort/wallet-schemas/ownership';
import {DownloadSimple, Stack} from '@phosphor-icons/react';
import type {FC} from 'react';
import {Fragment} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

type DigitalAssetListDisplayMode = 'grid' | 'carousel';

type DigitalAssetListProps = {
  displayMode: DigitalAssetListDisplayMode;
  pendingOrders: Array<OrderWDto>;
  ownerships: Array<OwnershipWDto>;
};

export const DigitalAssetList: FC<DigitalAssetListProps> = ({
  displayMode,
  pendingOrders,
  ownerships,
}) => {
  const {space} = useSpaceContext();
  const {hasDarkBg, hasDarkAccent} = useThemeContext();
  const {t} = useTranslation('components', {keyPrefix: 'space.digitalAssetList'});
  const navigate = useNavigate();
  const {isMobile} = useScreenSize();
  const {setTransitioning} = useSpaceContext();

  if (space === undefined) {
    return <Fragment />;
  }
  const digitalAssetsCount = pendingOrders.length + ownerships.length;

  if (digitalAssetsCount === 0) {
    return (
      <EmptySection
        icon={
          <Stack
            className={classNames(
              'block h-8 w-8 place-items-center',
              hasDarkBg ? 'text-white' : 'text-black'
            )}
            aria-hidden="true"
          />
        }
        title={t('emptyDigitalAssetListTitle', {brand: space.merchant.name})}
        subtitle={t('emptyDigitalAssetListSubtitle', {brand: space.merchant.name})}
        cta={
          <PrimaryButton
            testId="nft-import-button"
            text={t('importDigitalAssetButton')}
            className="px-6"
            onClick={() => {
              setTransitioning({
                active: true,
                enterFrom: 'left',
              });
              navigate(getImportNftRoute());
            }}
            icon={
              <DownloadSimple
                className="h-5 w-5"
                aria-hidden="true"
                style={{color: hasDarkAccent ? 'white' : 'black'}}
              />
            }
            tracking={{namespace: 'digitalAssets.list.import'}}
          />
        }
      />
    );
  }

  return (
    <div
      className={classNames(
        'flex gap-y-5 md:gap-x-5',
        displayMode === 'grid' && '-mx-1.5 flex-wrap md:mx-0', // Display the cards in carousel in preview.
        displayMode === 'carousel' && 'no-scrollbar -mx-4 overflow-x-auto px-2 md:mx-0 md:px-0'
      )}
    >
      {pendingOrders.map(({campaign, id, imageFileKey, animationFileKey, tokenId}, index) => (
        <div
          data-testid={`pending-nft-tile-${index}`}
          key={id}
          className={classNames(
            'flex aspect-square flex-col rounded-t-xl',
            isMobile ? 'w-1/2 px-2' : 'w-[var(--thin-card-width)]'
          )}
        >
          <DigitalAssetCard
            key={tokenId}
            name={campaign.collection.name}
            animationFileKey={animationFileKey !== null ? animationFileKey : undefined}
            tokenId={tokenId}
            imageFileKey={imageFileKey}
            transfering
          />
        </div>
      ))}

      {ownerships.map((activeOwnership, index) => (
        <div
          data-testid={`nft-tile-${index}`}
          key={activeOwnership.digitalAsset.id}
          className={classNames(
            'flex flex-col rounded-t-xl',
            isMobile ? 'w-1/2 px-2' : 'w-[var(--thin-card-width)]'
          )}
        >
          <DigitalAssetCard
            key={activeOwnership.digitalAsset.tokenId}
            name={activeOwnership.digitalAsset.collection.title}
            animationFileKey={activeOwnership.digitalAsset.animationFileKey ?? undefined}
            ownership={activeOwnership}
            tokenId={activeOwnership.digitalAsset.tokenId}
            imageFileKey={activeOwnership.digitalAsset.imageFileKey}
          />
        </div>
      ))}
    </div>
  );
};

export default DigitalAssetList;
